import { client, download, withDataRequest } from 'utils/client';
import { JobSeekerForm, JobSeekerFormData, CheckEmail, JobSeekersRequestData, JobSeekerTableView } from 'features/job-seekers/types';
import { RequestOptions } from 'http';
import { ShareLinkInfoResponse, ProgramJoinForm } from 'features/job-seekers-invitation/types';
import { JobSeeker } from 'features/job-seeker-auth';
import { PasswordForm } from 'features/users/types';
import { ProfileFormData } from 'features/job-seeker-profile';
import { JobSeekerRespondData } from 'features/job-seeker-respond';
import { ChangeToken, TableDataResponse } from 'features/types';
import { NotificationSettingsForm, NotificationSettingsFormData } from 'features/notification-settings';
import { JobSeekerOpportunitiesFiltersData } from 'features/job-seeker-opportunities';
import { UpdatePhoneForm } from 'components/UpdatePhone/types';
import { JobSeekerManageForm, JobSeekerManageFormData } from 'features/job-seeker-manage';
import { EmploymentStatusFormData, SetEmploymentStatusForm } from 'features/job-seeker-employment-status';
import { JobSeekerHomePageChecks, JobSeekerInitialEntryFormData } from 'features/job-seeker-initial-entry';
import { GetSelectList, GetSelectListResponse, Tab } from './types';
import { JobSeekerIsVerifiedForm, JobSeekerIsVerifiedFormData } from 'features/job-seeker-manage-for-manager';
import { JobSeekerJobSeekersTableRequestData, JobSeekerJobSeekersTableView, JobSeekerProfileRestrictedView } from 'features/job-seeker-job-seekers-table';
import { CommunityAdminJobSeekersTableRequestData } from 'features/manage-admins-table';
import { SavedEmployerSelectListResponse } from 'features/job-seekers/types';
import { JobSeekerResourcesTableView, ResourcesViewMode } from 'features/job-seeker-resources/types';

export type JobSeekerProfileGroupItem = {
  name: string;
  organizationName: string;
  isGroupLead: boolean;
  communityAdminRole?: string | null;
};

export type JobSeekerUpdateEmailForm = {
  email: string;
  emailConfirm?: string;
};

const jobSeekersAPI = {
  add(data: JobSeekerForm) {
    return client.post<void>('/job-seekers/add', withDataRequest(data));
  },
  addExisting(data: JobSeekerForm) {
    return client.post<void>(`/job-seekers/${data.id}/add-existing`, withDataRequest(data));
  },
  addForLeadJobSeeker(data: JobSeekerForm) {
    return client.post<void>('/job-seekers/add-for-lead-jobseeker', withDataRequest(data));
  },
  addExistingForLeadJobSeeker(data: JobSeekerForm) {
    return client.post<void>(`/job-seekers/${data.id}/add-existing-for-lead-jobseeker`, withDataRequest(data));
  },

  getJobSeekersTable(params: JobSeekersRequestData) {
    return client.post<TableDataResponse<JobSeekerTableView>>('/job-seekers/get-table', withDataRequest(params));
  },
  getJobSeekersTabsData(params: JobSeekersRequestData) {
    return client.post<Tab[]>('/job-seekers/get-tabs', withDataRequest(params));
  },
  getJobSeekerProfileView(jobSeekerId: string) {
    return client.get<JobSeekerJobSeekersTableView | JobSeekerProfileRestrictedView | null>(`/job-seekers/${jobSeekerId}/profile-view`);
  },
  getJobSeekerProfileGroups(jobSeekerId: string, page: number, pageSize: number) {
    return client.get<{ rows: JobSeekerProfileGroupItem[]; totalCount: number }>(`/job-seekers/${jobSeekerId}/profile-groups?page=${page}&pageSize=${pageSize}`);
  },
  getJobSeekerJobSeekersTable(params: JobSeekerJobSeekersTableRequestData) {
    return client.post<TableDataResponse<JobSeekerJobSeekersTableView>>('/job-seekers/get-job-seeker-job-seekers-table', withDataRequest(params));
  },
  getJobSeekerJobSeekersTabsData(params: JobSeekerJobSeekersTableRequestData) {
    return client.post<Tab[]>('/job-seekers/get-job-seeker-job-seekers-tabs', withDataRequest(params));
  },
  getCommunityAdminJobSeekersTable(params: CommunityAdminJobSeekersTableRequestData) {
    return client.post<TableDataResponse<JobSeekerTableView>>('/job-seekers/get-community-admins-table', withDataRequest(params));
  },
  getCommunityAdminJobSeekersTabsData(params: JobSeekerJobSeekersTableRequestData) {
    return client.post<Tab[]>('/job-seekers/get-community-admins-tabs', withDataRequest(params));
  },
  getManageFormData(jobSeekerId: string, onBehalfOfOrganizationId?: string | undefined) {
    if (onBehalfOfOrganizationId) {
      return client.get<JobSeekerManageFormData>(`/job-seekers/${jobSeekerId}/${onBehalfOfOrganizationId}/manage-form-data`);
    }
    return client.get<JobSeekerManageFormData>(`/job-seekers/${jobSeekerId}/manage-form-data`);
  },
  getManageForJobSeekerManagerFormData(jobSeekerId: string) {
    return client.get<JobSeekerIsVerifiedFormData>(`/job-seekers/${jobSeekerId}/manage-for-job-seeker-manager-form-data`);
  },
  manage(jobSeekerId: string, data: JobSeekerManageForm, onBehalfOfOrganizationId?: string | undefined) {
    if (onBehalfOfOrganizationId) {
      return client.put<void>(`/job-seekers/${jobSeekerId}/${onBehalfOfOrganizationId}/manage`, withDataRequest(data));
    }
    return client.put<void>(`/job-seekers/${jobSeekerId}/manage`, withDataRequest(data));
  },
  manageForJobSeekerManager(jobSeekerId: string, data: JobSeekerIsVerifiedForm) {
    return client.put<void>(`/job-seekers/${jobSeekerId}/manage-for-job-seeker-manager`, withDataRequest(data));
  },
  getManageCommunityAdminJobSeekerFormData(jobSeekerId: string) {
    return client.get<JobSeekerManageFormData>(`/job-seekers/${jobSeekerId}/manage-community-admin-form-data`);
  },
  manageCommunityAdminJobSeeker(jobSeekerId: string, data: JobSeekerManageForm) {
    return client.put<void>(`/job-seekers/${jobSeekerId}/manage-community-admin`, withDataRequest(data));
  },
  importJobSeekers(options: RequestOptions) {
    return client.post<void>('/job-seekers/bulk-import', options);
  },
  bulkImportForAdminMember(options: RequestOptions) {
    return client.post<void>('/job-seekers/bulk-import-for-admin-member', options);
  },
  findByEmail(data: CheckEmail & { onBehalfOfOrganizationId?: string; }) {
    if (data.onBehalfOfOrganizationId) {
      return client.post<JobSeekerFormData>('/job-seekers/find-by-email-for-lead-jobseeker', withDataRequest(data));
    }
    return client.post<JobSeekerFormData>('/job-seekers/find-by-email', withDataRequest(data));
  },
  current() {
    return client.get<JobSeeker>('/job-seekers/current');
  },
  updatePassword(data: PasswordForm) {
    return client.put<void>('/job-seekers/update-password', withDataRequest(data));
  },
  editProfile(params: FormData) {
    return client.put<void>('/job-seekers/edit-profile', withDataRequest(params));
  },
  getProfileFormData() {
    return client.get<ProfileFormData>('/job-seekers/get-profile-form-data');
  },
  downloadResume() {
    return download('job-seekers/download-resume');
  },
  getRespondDataByOrganization(jobSeekerId: string) {
    return client.get<JobSeekerRespondData>(`/job-seekers/${jobSeekerId}/respond-data`);
  },
  getRespondDataByJobSeeker() {
    return client.get<JobSeekerRespondData>(`/job-seekers/respond-data`);
  },
  updateEmail(data: JobSeekerUpdateEmailForm) {
    return client.put<void>('/job-seekers/update-email', withDataRequest(data));
  },
  confirmEmailChange(data: ChangeToken) {
    return client.post<void>('/job-seekers/confirm-email-change', withDataRequest(data));
  },
  updatePhone(data: UpdatePhoneForm) {
    return client.put<void>('/job-seekers/update-phone', withDataRequest(data));
  },
  confirmPhoneChange(data: ChangeToken) {
    return client.post<void>('/job-seekers/confirm-phone-change', withDataRequest(data));
  },
  getShareLinkInfo(token: string) {
    return client.get<ShareLinkInfoResponse>(`auth/job-seeker/share-link-info/${token}`);
  },
  applyShareLink(token: string) {
    return client.put<void>(`/job-seekers/apply-share-link`, withDataRequest({ token }));
  },
  applyShareLinkAndJoinProgram(data: ProgramJoinForm) {
    return client.put<void>(`/job-seekers/apply-share-link`, withDataRequest(data));
  },
  getJobSeekerEmailsSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>('/job-seekers/get-emails-select-list', withDataRequest(params));
  },
  getNotificationSettingsFormData() {
    return client.get<NotificationSettingsFormData>(`/job-seekers/get-notification-settings-form-data`);
  },
  editNotificationSettings(data: NotificationSettingsForm) {
    return client.put<void>('/job-seekers/edit-notification-settings', withDataRequest(data));
  },
  getOpportunitiesFiltersData() {
    return client.get<JobSeekerOpportunitiesFiltersData>('/job-seekers/get-opportunities-filters-data');
  },
  homePageChecks() {
    return client.get<JobSeekerHomePageChecks>('/job-seekers/home-page-checks');
  },
  setEmploymentStatus(data: SetEmploymentStatusForm) {
    return client.put<void>('/job-seekers/set-employment-status', withDataRequest(data));
  },
  getEmploymentStatusData() {
    return client.get<EmploymentStatusFormData>('/job-seekers/employment-status-data');
  },
  getInitialEntryFormData() {
    return client.get<JobSeekerInitialEntryFormData>('/job-seekers/initial-entry-form-data');
  },
  saveInitialEntryData(data: FormData) {
    return client.put<void>('/job-seekers/save-initial-entry-data', withDataRequest(data));
  },
  getSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>('/job-seekers/get-select-list', withDataRequest(params));
  },
  getApplicantsSelectList(params: GetSelectList) {
    return client.post<GetSelectListResponse>('/job-seekers/get-applicants-select-list', withDataRequest(params));
  },
  addSaveSavedEmployer(data: FormData) {
    return client.put<void>('/job-seekers/add-saved-employer', withDataRequest(data));
  },
  removeSavedEmployer(jobSeekerId: string, savedEmployerId: string) {
    return client.delete<void>(`/job-seekers/${jobSeekerId}/${savedEmployerId}/remove-saved-employer`);
  },
  getSavedEmployersSelectList(params: GetSelectList) {
    return client.post<SavedEmployerSelectListResponse>('/job-seekers/get-saved-employers-select-list', withDataRequest(params));
  },
  resendInviteEmail(jobSeekerId: string) {
    return client.post<void>(`/job-seekers/${jobSeekerId}/resend-invite-email`);
  },
  resendInviteEmailForLeadJobSeeker(jobSeekerId: string, onBehalfOfOrganizationId: string | undefined) {
    return client.post<void>(`/job-seekers/${jobSeekerId}/${onBehalfOfOrganizationId}/resend-invite-email`);
  },
  removeFromCommunity(jobSeekerId: string) {
    return client.delete<void>(`/job-seekers/${jobSeekerId}/remove-from-community`);
  },
  removeFromCommunityForLeadJobSeeker(jobSeekerId: string, onBehalfOfOrganizationId: string | undefined) {
    return client.delete<void>(`/job-seekers/${jobSeekerId}/${onBehalfOfOrganizationId}/remove-from-community`);
  },
  getJobSeekerProfileResources(jobSeekerId: string, viewMode: ResourcesViewMode, page: number, pageSize: number, eventFilter?: 'upcoming' | 'past') {
    const params = new URLSearchParams({ viewMode, page: String(page), pageSize: String(pageSize) });
    if (eventFilter) params.set('eventFilter', eventFilter);
    return client.get<{ rows: JobSeekerResourcesTableView[]; totalCount: number }>(`/job-seekers/${jobSeekerId}/profile-resources?${params.toString()}`);
  },
};

export default jobSeekersAPI;
